import type { ServiceFetcher } from '../shared/http.types';
import type {
  BankFolder,
  CreateFolderResponse,
  FolderActionResponse,
  FolderMembershipPayload,
} from './folders.types';

const FOLDERS_PATH = 'folders';
const CONNECTIONS_PATH = 'connections';

export function getFolders(fetcher: ServiceFetcher): Promise<BankFolder[]> {
  return fetcher({ method: 'GET', path: FOLDERS_PATH }) as Promise<BankFolder[]>;
}

export function createFolder(
  fetcher: ServiceFetcher,
  title: string,
): Promise<CreateFolderResponse> {
  return fetcher({
    method: 'POST',
    path: FOLDERS_PATH,
    body: { title },
  }) as Promise<CreateFolderResponse>;
}

/** PATCH /folders/:id — rename. */
export function updateFolder(
  fetcher: ServiceFetcher,
  id: number,
  title: string,
): Promise<BankFolder> {
  return fetcher({
    method: 'PATCH',
    path: `${FOLDERS_PATH}/${id}`,
    body: { title },
  }) as Promise<BankFolder>;
}

/**
 * PATCH /folders/reorder — `ids` is the caller's full folder list in its new
 * order; the server derives each `position` from the index, so no client has
 * to compute ranks. Ids the caller doesn't own are ignored rather than
 * rejected, so a folder deleted on another device can't fail the whole drag.
 * Resolves to the re-sorted list.
 */
export function reorderFolders(
  fetcher: ServiceFetcher,
  ids: number[],
): Promise<BankFolder[]> {
  return fetcher({
    method: 'PATCH',
    path: `${FOLDERS_PATH}/reorder`,
    body: { ids },
  }) as Promise<BankFolder[]>;
}

export function deleteFolder(fetcher: ServiceFetcher, id: number): Promise<unknown> {
  return fetcher({ method: 'DELETE', path: `${FOLDERS_PATH}/${id}` });
}

export function addToFolder(
  fetcher: ServiceFetcher,
  folderId: number,
  payload: FolderMembershipPayload,
): Promise<FolderActionResponse> {
  return fetcher({
    method: 'POST',
    path: `${CONNECTIONS_PATH}/add-to-folder/${folderId}`,
    body: payload,
  }) as Promise<FolderActionResponse>;
}

export function removeFromFolder(
  fetcher: ServiceFetcher,
  folderId: number,
  payload: FolderMembershipPayload,
): Promise<FolderActionResponse> {
  return fetcher({
    method: 'POST',
    path: `${CONNECTIONS_PATH}/remove-from-folder/${folderId}`,
    body: payload,
  }) as Promise<FolderActionResponse>;
}
